/**
 * Hilfsfunktionen für das Urlaubskonto.
 *
 * Das Urlaubskonto einer Beschäftigung ist ein Array von Urlaub-Objekten,
 * absteigend nach UrlaubJahr sortiert (Index 0 = aktuellstes Jahr).
 * Für die Abwärtskompatibilität (noch nicht migrierte Stammdaten, historische
 * Journal-Snapshots) wird ein einzelnes Objekt weiterhin akzeptiert und wie das
 * aktuelle Jahr behandelt.
 */

/**
 * Gibt das Urlaub-Objekt eines bestimmten Jahres zurück (oder das aktuellste).
 * Fallback: ist `Urlaub` noch ein Objekt (altes Format), wird es direkt zurückgegeben.
 * Das Objekt kommt per Referenz zurück, Schreibzugriffe wirken also auf das Stammdatum.
 * @param {object} traeger Objekt mit einem `.Urlaub` Feld (Beschäftigung oder aggregiertes MA-Objekt)
 * @param {number} [jahr] gewünschtes Urlaubsjahr; ohne Angabe das aktuellste
 * @returns {object} Urlaub-Objekt
 */
export function getUrlaub(traeger, jahr) {
    const urlaub = traeger?.Urlaub;
    if (!Array.isArray(urlaub)) {
        return urlaub || {};
    }
    if (jahr != null) {
        const treffer = urlaub.find((u) => u.UrlaubJahr === jahr);
        if (treffer) {
            return treffer;
        }
    }
    return urlaub[0] || {};
}

/**
 * Gibt das Urlaubskonto immer als Array zurück, ohne das Stammdatum zu verändern.
 * Praktisch für Anzeige/Iteration mit Fallback auf das alte Objektformat.
 * @param {object} traeger Objekt mit einem `.Urlaub` Feld
 * @returns {object[]}
 */
export function getUrlaubListe(traeger) {
    const urlaub = traeger?.Urlaub;
    if (Array.isArray(urlaub)) {
        return urlaub;
    }
    if (urlaub && Object.keys(urlaub).length > 0) {
        return [urlaub];
    }
    return [];
}

/**
 * Normalisiert ein einzelnes Urlaub-Objekt: Sonderurlaub muss ein Array sein.
 * @param {object} urlaub
 */
function normalisiereUrlaubseintrag(urlaub) {
    if (!Array.isArray(urlaub.Sonderurlaub)) {
        // Der alte Zahlenwert (z.B. 0) wird verworfen, Sonderurlaub startet als leeres Array.
        urlaub.Sonderurlaub = [];
    }
}

/**
 * Gibt den Urlaubseintrag des aktuellen Kalenderjahres zurück (Fallback: aktuellstes Jahr).
 * Maßgeblich für `Uebersicht.Resturlaub`, der immer das laufende Jahr widerspiegeln soll –
 * auch wenn bereits ein Eintrag für ein zukünftiges Jahr existiert.
 * @param {object} traeger Objekt mit einem `.Urlaub` Feld
 * @param {number} [aktuellesJahr] aktuelles Jahr (Standard: heutiges Kalenderjahr)
 * @returns {object} Urlaubseintrag des laufenden Jahres
 */
export function getUrlaubAktuellesJahr(traeger, aktuellesJahr = new Date().getFullYear()) {
    return getUrlaub(traeger, aktuellesJahr);
}

/**
 * Summiert die Sonderurlaubstage eines Urlaubseintrags.
 * @param {object} urlaub Urlaubseintrag mit `.Sonderurlaub` Array aus { SonderurlaubTage, Notiz }
 * @returns {number} Summe der Sonderurlaubstage (0, falls keine vorhanden)
 */
export function getSonderurlaubSumme(urlaub) {
    if (!urlaub || !Array.isArray(urlaub.Sonderurlaub)) {
        return 0;
    }
    return urlaub.Sonderurlaub.reduce((summe, su) => summe + (Number(su.SonderurlaubTage) || 0), 0);
}

/**
 * Migriert das Urlaubskonto einer Beschäftigung auf das Array-Format.
 * - Objekt -> [Objekt]
 * - Sonderurlaub (Zahl) -> [] (Array aus { SonderurlaubTage, Notiz })
 * - absteigend nach UrlaubJahr sortiert
 * Idempotent: bereits migrierte Daten werden nur normalisiert und nachsortiert.
 * @param {object} beschaeftigung
 */
export function migriereUrlaub(beschaeftigung) {
    if (!beschaeftigung) {
        return;
    }
    const liste = getUrlaubListe(beschaeftigung);
    liste.forEach(normalisiereUrlaubseintrag);
    liste.sort((a, b) => (b.UrlaubJahr || 0) - (a.UrlaubJahr || 0));
    beschaeftigung.Urlaub = liste;
}

/**
 * Stellt sicher, dass für `neuesJahr` ein Urlaubseintrag existiert. Fehlt er,
 * wird er als Kopie des aktuellsten Jahres angelegt (Sonderurlaub und genommene Urlaubstage
 * starten leer/0) und dem Array vorangestellt, sodass Jahresübergänge nachvollziehbar bleiben.
 *
 * Der Übertrag des Resturlaubs (`ResturlaubLaufendesJahr` des Vorjahres -> `ResturlaubVorwert`)
 * ist ein echter Jahresübergang und gehört nur in kontrollierte Kontexte (Dezember-Abschluss,
 * erzwungener Jahreswechsel im Februar). Wird `uebernehmeVorwert = false` übergeben (z.B. wenn
 * das laufende Jahr lediglich nachgetragen wird), startet der neue Eintrag mit Vorwert 0.
 * @param {object} beschaeftigung
 * @param {number} neuesJahr
 * @param {boolean} [uebernehmeVorwert=true] Resturlaub des Vorjahres als Vorwert übernehmen
 * @returns {object} der Urlaubseintrag des neuen Jahres
 */
export function rolloverUrlaubsjahr(beschaeftigung, neuesJahr, uebernehmeVorwert = true) {
    migriereUrlaub(beschaeftigung);
    const liste = beschaeftigung.Urlaub;
    const vorhanden = liste.find((u) => u.UrlaubJahr === neuesJahr);
    if (vorhanden) {
        return vorhanden;
    }
    const vorjahr = liste[0] || {};
    const eintrag = {
        ...vorjahr,
        UrlaubJahr: neuesJahr,
        ResturlaubVorwert: uebernehmeVorwert ? (vorjahr.ResturlaubLaufendesJahr || 0) : 0,
        // Genommene Urlaubstage gelten je Jahr und werden nie übernommen (die kontrollierten
        // Jahreswechsel-Pfade berechnen sie ohnehin direkt im Anschluss neu).
        UrlaubGenehmigtSumme: 0,
        Sonderurlaub: [],
    };
    liste.unshift(eintrag);
    liste.sort((a, b) => (b.UrlaubJahr || 0) - (a.UrlaubJahr || 0));
    return eintrag;
}
